import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Role, RolePayload, RolesResponse, RoleFilters } from './types';
import { fetchRoles, createRole, updateRole, deleteRole } from './rolesApi';

interface RolesState {
  items: Role[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    pages: number;
  };
  sortBy: string;
  sortDir: 'asc' | 'desc';
  search: string;
}

const initialState: RolesState = {
  items: [],
  loading: false,
  error: null,
  pagination: { page: 1, pageSize: 20, total: 0, pages: 0 },
  sortBy: 'name',
  sortDir: 'asc',
  search: ''
};

export const loadRoles = createAsyncThunk<RolesResponse, RoleFilters | undefined>(
  'roles/load',
  async (params) => fetchRoles(params)
);
export const addRole = createAsyncThunk<Role, RolePayload>('roles/add', async (payload) => createRole(payload));
export const editRole = createAsyncThunk<Role, { id: string; payload: RolePayload }>(
  'roles/edit',
  async ({ id, payload }) => updateRole(id, payload)
);
export const removeRole = createAsyncThunk<string, string>('roles/remove', async (id) => {
  await deleteRole(id);
  return id;
});

const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    setRolesSort(state, action: PayloadAction<{ sortBy: string; sortDir: 'asc' | 'desc' }>) {
      state.sortBy = action.payload.sortBy;
      state.sortDir = action.payload.sortDir;
    },
    setRolesPage(state, action: PayloadAction<number>) {
      state.pagination.page = action.payload;
    },
    setRolesPageSize(state, action: PayloadAction<number>) {
      state.pagination.pageSize = action.payload;
    },
    setRolesSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadRoles.fulfilled, (state, action: PayloadAction<RolesResponse>) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(loadRoles.rejected, (state) => {
        state.loading = false;
        state.error = 'No se pudieron cargar los roles';
      })
      .addCase(addRole.fulfilled, (state, action) => {
        state.items = [...state.items, action.payload].sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(editRole.fulfilled, (state, action) => {
        state.items = state.items
          .map((r) => (r.id === action.payload.id ? action.payload : r))
          .sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(removeRole.fulfilled, (state, action) => {
        state.items = state.items.filter((r) => r.id !== action.payload);
      });
  }
});

export const { setRolesSort, setRolesPage, setRolesPageSize, setRolesSearch } = rolesSlice.actions;

export default rolesSlice.reducer;

